import type {ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {EventDropdown} from '@Components/event-wizard/event-dropdown';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {ALIGNMENT_TYPE, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {WeekDaySelectors} from '@Components/event-wizard/week-day-selectors';
import {MonthDaySelectors} from '@Components/event-wizard/month-day-selectors';
import capitalize from 'lodash/capitalize';
import {ClickableDiv} from '@Components/clickable-div';
import {usePopover} from '@Components/popover/hooks/usePopover';
import {PopoverDefaultAlignment} from '@Components/popover/popover.types';
import {onEventWizardDateTimeMobilePanelUpdate, onEventWizardRecurringEndsOnChange, onEventWizardToggleRecurringChange} from '@Components/event-wizard/event-wizard-slice';
import {useDispatch} from 'react-redux';
import {
  useEventWizardEndDateTime,
  useEventWizardIsRecurring,
  useEventWizardRecurringData,
  useEventWizardRecurringLastDate,
  useEventWizardRecurringType, useEventWizardShouldUpsellRecurring,
  useEventWizardStartDateTime,
  useEventWizardTimeZone,
} from '@Components/event-wizard/event-wizard.hooks';
import {updatePreviewDateTime} from '@Components/event-wizard/preview/preview.helper';
import {EventWizardPremiumIcon} from '@Components/event-wizard/event-wizard-premium-icon';
import {EventWizardRecurringTypeContent} from '@Components/event-wizard/event-wizard-recurring-type-content';
import {DateTimeMobilePanel} from '@Panels/event-wizard-date-time-panel/event-wizard-date-time-panel.types';
import {DateInputCalendarContent} from '@Components/event-wizard/date-input-calendar-content';
import {getMaxRecurringDate} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.helper';
import styles from './recurring-event-selector.module.scss';
import type {RecurringEventSelectorProps} from './recurring-event-selector.types';
import {RecurringType} from './recurring-event-selector.types';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';
import {openEventsUpsellDialog, EventUpsellType} from '@Libraries/events.library';

export function RecurringEventSelector({endDate, isMobile = false, onToggleRecurring = undefined}: RecurringEventSelectorProps): ReactElement<any> {
  const dispatch = useDispatch();
  const calendarRef = useRef<HTMLDivElement>(null);
  const {isPopoverOpen, togglePopover, renderPopover} = usePopover({referenceElementRef: calendarRef, alignment: PopoverDefaultAlignment.RIGHT_START, className: styles.popover});

  const selectedStartDateTime = useEventWizardStartDateTime();
  const selectedEndDateTime = useEventWizardEndDateTime();
  const recurringPattern = useEventWizardRecurringData();
  const isRepeatOn = useEventWizardIsRecurring();
  const recurringType = useEventWizardRecurringType();
  const lastRecurringDate = useEventWizardRecurringLastDate();
  const selectedTimezone = useEventWizardTimeZone();
  const shouldUpsellRecurring = useEventWizardShouldUpsellRecurring();

  const [isRecurringTypeDropdownOpen, setIsRecurringTypeDropdownOpen] = useState<boolean>(false);

  const toggleIsRepeated = (): void => {
    if (shouldUpsellRecurring) {
        openEventsUpsellDialog(EventUpsellType.RECURRING, undefined);
      return;
    }
    dispatch(onEventWizardToggleRecurringChange(!isRepeatOn));

    updatePreviewDateTime({
      startDateTime: selectedStartDateTime,
      endDateTime: selectedEndDateTime,
      recurringPattern: {
        ...recurringPattern,
        isRecurring: !isRepeatOn,
      },
      timeZone: selectedTimezone,
    });

    if (onToggleRecurring) {
      onToggleRecurring(!isRepeatOn);
    }
  };

  const updateLastRecurringDate = (newDate: Date): void => {
    dispatch(onEventWizardRecurringEndsOnChange(newDate));
  };

  const toggleDatePickerVisibility = (): void => {
    if (isMobile) {
      dispatch(onEventWizardDateTimeMobilePanelUpdate(DateTimeMobilePanel.RECURRING_ENDS_ON_SELECTOR));
    } else {
      togglePopover();
    }
  };

  const getRecurringEndsOnSelectorLabel = (date: Date): string => {
    const day = date.getDate();
    const month = date.toLocaleString('en-US', {month: 'short'});
    const year = date.getFullYear().toString().slice(-2);

    const daySuffix = (dayToSuffix: number): string => {
      if (dayToSuffix > 3 && dayToSuffix < 21) return 'th';
      switch (dayToSuffix % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    return `${day}${daySuffix(day)} ${month} '${year}`;
  };

  const getRecurringTypeOptions = (): ReactElement<any> => {
    return (
      <div className={styles.marginLeftAuto}>
        <EventDropdown
          selector={getRecurringTypeDropdownSelector()}
          popup={<EventWizardRecurringTypeContent onSelect={onSelectRecurringType} />}
          popupClass={`${styles.popup} ${styles.recurringTypePopup}`}
          popUpHasCustomWidth
          alignment={ALIGNMENT_TYPE.RIGHT_START}
          open={isRecurringTypeDropdownOpen}
          onOpen={() => {
            if (isMobile) {
              dispatch(onEventWizardDateTimeMobilePanelUpdate(DateTimeMobilePanel.RECURRING_TYPE_SELECTOR));
            } else {
              setIsRecurringTypeDropdownOpen(true);
            }
          }}
          onClose={() => {
            setIsRecurringTypeDropdownOpen(false);
          }}
        />
      </div>
    );
  };

  const getRecurringTypeDropdownSelector = (): ReactElement<any> => {
    return <DropdownGhostSelectBox size={DROPDOWN_SIZE.XSMALL} textClassName="spacing-m-r-2" openedClassName={styles.dropdownSelectorOpened} text={capitalize(recurringType)} />;
  };

  const onSelectRecurringType = (): void => {
    setIsRecurringTypeDropdownOpen(false);
  };

  const getRecurringContentByType = (): ReactElement<any> | undefined => {
    const isMonthlyOrWeekly = recurringType === RecurringType.MONTHLY || recurringType === RecurringType.WEEKLY;

    return (
      <div className="spacing-m-t-1 spacing-p-t-2 spacing-p-b-2 spacing-p-l-3 spacing-p-r-3 bg-neutral-1 border-s-standard _no-border-bottom _no-border-left _no-border-right _full-width">
        {isMonthlyOrWeekly ? (
          <div className={isMobile && recurringType === RecurringType.WEEKLY ? 'spacing-m-b-3' : 'flex-row-align-center justify-content-between spacing-m-b-1'}>
            <div className={`${isMobile ? 'spacing-m-b-2' : ''} spacing-p-r-8 flex-row-align-center`}>
              <Icon className="spacing-m-r-1" icon="icon-calendar" size={IconSize.SIZE_ICON_16} />
              <Text className={styles.textNoWrap} val={window.i18next.t('pmwjs_occurs_every')} size={TextSize.XXSMALL} bold />
            </div>
            {recurringType === RecurringType.MONTHLY ? <MonthDaySelectors /> : <WeekDaySelectors className="_full-width justify-content-center" />}
          </div>
        ) : undefined}
        {getRecurringEndsOnContent()}
      </div>
    );
  };

  const getRecurringEndsOnContent = (): ReactElement<any> => {
    return (
      <div className="flex-row-align-center">
        <Icon className="spacing-m-r-1" icon="icon-h-align-right" size={IconSize.SIZE_ICON_16} />
        <Text val={window.i18next.t('pmwjs_ends_on')} size={TextSize.XXSMALL} bold />
        <div className={styles.marginLeftAuto}>
          <ClickableDiv
            className={`${styles.dateInputContent} ${isPopoverOpen ? styles.popOverOpenBorder : ''} flex-row-align-center radius-4`}
            onClick={toggleDatePickerVisibility}
            ref={calendarRef}
          >
            <Text className="content-body" val={getRecurringEndsOnSelectorLabel(lastRecurringDate)} size={TextSize.XXSMALL} />
            <Icon className="spacing-m-l-2" icon="icon-caret-down" size={IconSize.SIZE_ICON_16} />
          </ClickableDiv>
          {!isMobile
            ? renderPopover(
                <DateInputCalendarContent
                  minDate={endDate}
                  maxDate={getMaxRecurringDate(selectedStartDateTime, recurringType)}
                  selectedDate={lastRecurringDate}
                  onDateSelected={updateLastRecurringDate}
                />
              )
            : undefined}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={`${styles.switchContainer} flex-row-align-center _full-width`}>
        <Icon className="spacing-m-r-1" icon="icon-repeat" size={IconSize.SIZE_ICON_16} />
        <div className="flex-row-align-center spacing-m-r-3">
          <Text val={window.i18next.t('pmwjs_repeat')} size={TextSize.XXSMALL} bold />
          {shouldUpsellRecurring ? <EventWizardPremiumIcon upsellType={EventUpsellType.RECURRING} /> : undefined}
        </div>
        <ToggleSwitch isChecked={shouldUpsellRecurring ? false : isRepeatOn} onChange={toggleIsRepeated} size={ToggleSwitchV2Size.SMALL} />

        {isRepeatOn ? getRecurringTypeOptions() : undefined}
      </div>

      {isRepeatOn ? getRecurringContentByType() : undefined}
    </>
  );
}
